import React, { forwardRef } from "react";
import { BusyStateProps, RenderProp, ThemeColor } from "../../props";
import { CircularProgress } from "../CircularProgress";
import { ButtonBase, ButtonBaseProps } from "./ButtonBase";
import { Icon, InternalItemProps } from "../Icon";

const renderBusySymbol: RenderProp = ({ sizeClassName }) => (
  <CircularProgress className={sizeClassName} color={ThemeColor.none} />
);

export interface ButtonProps
  extends ButtonBaseProps,
    InternalItemProps,
    BusyStateProps {}

/**
 * Unified Button component with icon and busy state support.
 * Supports SVG icons, font icons, localized icon name, and busy/loading states.
 * Automatically handles icon display and switches to spinner when busy.
 * Ideal for all button use cases - from simple text buttons to complex icon buttons with loading states.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ iconPath, iconName, iconLocal, busy = false, ...props }, ref) => {
    let renderSymbol;

    if (busy) {
      renderSymbol = renderBusySymbol;
    } else if (iconPath || iconName || iconLocal) {
      renderSymbol = ({ sizeClassName }) => (
        <Icon
          path={iconPath}
          name={iconName}
          local={iconLocal}
          className={sizeClassName}
          color={ThemeColor.none}
        />
      );
    } else {
      renderSymbol = props.renderSymbol;
    }

    return (
      <ButtonBase
        ref={ref}
        {...props}
        renderSymbol={props.renderSymbol ?? renderSymbol}
      />
    );
  }
);

Button.displayName = "Button";
